import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Button, Card, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, FormControlLabel, Grid, IconButton, MenuItem, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, Delete, Edit, Search } from '@mui/icons-material';
import { api } from '../api/client.js';
import { crudResources } from '../config/crudResources.js';
import PageHeader from '../components/PageHeader.jsx';
import { formatApiError, useToast } from '../contexts/ToastContext.jsx';
import { DATE_FORMAT_HINT, formatDate, parseDateInput } from '../utils/date.js';
import { capitalizeWords, shouldCapitalizeField } from '../utils/text.js';
import { isCnicField, isPhoneField, maskCnic, maskFieldValue, maskPhone } from '../utils/mask.js';

export default function EntityPage({ resource, config, title, description, endpoint }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [deleting, setDeleting] = useState(null);
  const [quickAddField, setQuickAddField] = useState(null);
  const [quickAddForm, setQuickAddForm] = useState({});
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const fieldRefs = useRef({});
  const quickAddNameRef = useRef(null);
  const isCrud = Boolean(resource && config);
  const queryKey = isCrud ? ['resource', resource] : ['entity', endpoint];
  const requestUrl = isCrud ? `/resources/${resource}` : endpoint;
  const searchable = Boolean(config?.searchFields?.length);
  const listFilters = useMemo(() => config?.filters || [], [config?.filters]);
  const quickAddConfig = quickAddField ? crudResources[quickAddField.resource] : null;

  const { data = [], error } = useQuery({
    queryKey,
    queryFn: async () => (await api.get(requestUrl)).data
  });

  const referenceFields = useMemo(() => {
    const fromForm = (config?.fields || []).filter((field) => field.resource);
    const fromFilters = listFilters.filter((field) => field.resource);
    const fromQuickAddTargets = (config?.fields || [])
      .filter((field) => field.quickAdd && field.resource)
      .flatMap((field) => (crudResources[field.resource]?.fields || []).filter((item) => item.resource));
    const seen = new Set();
    return [...fromForm, ...fromFilters, ...fromQuickAddTargets].filter((field) => {
      const key = `${field.name}:${field.resource}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [config, listFilters]);
  const referenceQueries = useQueries({
    queries: referenceFields.map((field) => ({
      queryKey: ['resource', field.resource],
      queryFn: async () => (await api.get(`/resources/${field.resource}`)).data,
      staleTime: 60000
    }))
  });
  const references = Object.fromEntries(
    referenceFields.map((field, index) => [field.name, referenceQueries[index]?.data || []])
  );
  const quickAddReferences = useMemo(() => Object.fromEntries(
    (quickAddConfig?.fields || [])
      .filter((field) => field.resource)
      .map((field) => {
        const index = referenceFields.findIndex(
          (item) => item.name === field.name && item.resource === field.resource
        );
        return [field.name, index >= 0 ? (referenceQueries[index]?.data || []) : []];
      })
  ), [quickAddConfig, referenceFields, referenceQueries]);

  const filteredData = useMemo(() => {
    let rows = data;
    if (searchable) {
      const term = search.trim().toLowerCase();
      if (term) {
        rows = rows.filter((row) =>
          config.searchFields.some((field) => String(row[field] ?? '').toLowerCase().includes(term))
        );
      }
    }
    for (const filter of listFilters) {
      const selected = filters[filter.name];
      if (selected === undefined || selected === '') continue;
      rows = rows.filter((row) => {
        const value = row[filter.name];
        if (filter.name === 'isActive' || typeof value === 'boolean') {
          return String(Boolean(value)) === String(selected);
        }
        return String(value ?? '') === String(selected);
      });
    }
    return rows;
  }, [config?.searchFields, data, filters, listFilters, search, searchable]);

  const columns = isCrud
    ? config.columns
    : (data.length ? Object.keys(data[0]).map((key) => [key, splitName(key)]) : []);

  useEffect(() => {
    if (!dialogOpen || editing) return;
    setForm((current) => {
      if (String(current.balance ?? '') === String(current.openingBalance ?? '')) return current;
      if (current.openingBalance === undefined) return current;
      return { ...current, balance: current.openingBalance || 0 };
    });
  }, [dialogOpen, editing, form.openingBalance]);

  useEffect(() => {
    if (!dialogOpen || !config?.focusField) return undefined;
    const timer = window.setTimeout(() => fieldRefs.current[config.focusField]?.focus?.(), 50);
    return () => window.clearTimeout(timer);
  }, [dialogOpen, editing, config?.focusField]);

  const save = useMutation({
    mutationFn: async () => {
      for (const field of config.fields) {
        if (!isFieldVisible(field, form)) continue;
        const required = field.required || matchesCondition(field.requiredWhen, form);
        if (required && isBlank(form[field.name])) {
          throw new ClientValidationError(`${field.label} is required`);
        }
      }
      if (typeof config.validate === 'function') {
        const validationMessage = config.validate(form);
        if (validationMessage) throw new ClientValidationError(validationMessage);
      }
      const payload = {};
      for (const field of config.fields.filter((item) => isFieldVisible(item, form) && !item.readOnly)) {
        if (field.type === 'image') continue;
        if (field.type === 'date' && form[field.name]) {
          const parsed = parseDateInput(form[field.name]);
          if (!parsed) throw new ClientValidationError(`${field.label} must use ${DATE_FORMAT_HINT}`);
          payload[field.name] = parsed;
        } else {
          payload[field.name] = form[field.name];
        }
      }
      if (form.paymentTerms && form.paymentTerms !== 'Credit') payload.creditDays = null;
      if (!editing && form.openingBalance !== undefined) payload.openingBalance = form.openingBalance;
      const url = editing ? `/resources/${resource}/${editing.id}` : `/resources/${resource}`;
      const method = editing ? 'put' : 'post';
      const imageField = config.fields.find((field) => field.type === 'image');
      if (imageField && form[imageField.name] instanceof File) {
        const multipart = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== undefined && value !== null) multipart.append(key, value);
        });
        multipart.append(imageField.name, form[imageField.name]);
        return api[method](url, multipart, { skipErrorToast: true });
      }
      return api[method](url, payload, { skipErrorToast: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resource', resource] });
      setDialogOpen(false);
      toast.success(`${config.singular} ${editing ? 'updated' : 'saved'} successfully`);
    },
    onError: (mutationError) => {
      const message = mutationError instanceof ClientValidationError
        ? mutationError.message
        : formatApiError(mutationError, `Unable to save ${config.singular.toLowerCase()}`);
      toast[mutationError instanceof ClientValidationError ? 'warning' : 'error'](message);
    }
  });

  useEffect(() => {
    if (!dialogOpen || quickAddField || deleting) return undefined;
    const onKeyDown = (event) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const key = event.key.toLowerCase();
      if (key === 'c') {
        event.preventDefault();
        setDialogOpen(false);
      } else if (key === 'u' && !save.isPending) {
        event.preventDefault();
        save.mutate();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dialogOpen, quickAddField, deleting, save]);

  const remove = useMutation({
    mutationFn: async () => api.delete(`/resources/${resource}/${deleting.id}`, { skipErrorToast: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resource', resource] });
      setDeleting(null);
      toast.success(`${config.singular} removed successfully`);
    },
    onError: (mutationError) => toast.error(formatApiError(mutationError, `Unable to remove ${config.singular.toLowerCase()}`))
  });

  const quickAddSave = useMutation({
    mutationFn: async () => {
      if (!quickAddField || !quickAddConfig) throw new ClientValidationError('Quick add is unavailable');
      for (const field of quickAddConfig.fields) {
        if (!isFieldVisible(field, quickAddForm) || field.readOnly) continue;
        const required = field.required || matchesCondition(field.requiredWhen, quickAddForm);
        if (required && isBlank(quickAddForm[field.name])) {
          throw new ClientValidationError(`${field.label} is required`);
        }
      }
      const payload = {};
      for (const field of quickAddConfig.fields.filter((item) => isFieldVisible(item, quickAddForm) && !item.readOnly)) {
        payload[field.name] = quickAddForm[field.name];
      }
      if (quickAddForm.paymentTerms && quickAddForm.paymentTerms !== 'Credit') payload.creditDays = null;
      if (quickAddForm.openingBalance !== undefined) payload.openingBalance = quickAddForm.openingBalance;
      return api.post(`/resources/${quickAddField.resource}`, payload, { skipErrorToast: true });
    },
    onSuccess: async (response) => {
      const createdId = response.data?.id;
      await queryClient.invalidateQueries({ queryKey: ['resource', quickAddField.resource] });
      if (createdId !== undefined && createdId !== null) {
        setForm((current) => ({ ...current, [quickAddField.name]: createdId }));
      }
      setQuickAddField(null);
      toast.success(`${quickAddConfig.singular} saved successfully`);
    },
    onError: (mutationError) => {
      const message = mutationError instanceof ClientValidationError
        ? mutationError.message
        : formatApiError(mutationError, `Unable to save ${quickAddConfig?.singular?.toLowerCase() || 'record'}`);
      toast[mutationError instanceof ClientValidationError ? 'warning' : 'error'](message);
    }
  });

  useEffect(() => {
    if (!quickAddField) return undefined;
    const timer = window.setTimeout(() => quickAddNameRef.current?.focus?.(), 50);
    return () => window.clearTimeout(timer);
  }, [quickAddField]);

  useEffect(() => {
    if (!quickAddField) return;
    setQuickAddForm((current) => {
      if (String(current.balance ?? '') === String(current.openingBalance ?? '')) return current;
      if (current.openingBalance === undefined) return current;
      return { ...current, balance: current.openingBalance || 0 };
    });
  }, [quickAddField, quickAddForm.openingBalance]);

  function openQuickAdd(field) {
    const related = crudResources[field.resource];
    if (!related) {
      toast.error('Unable to add related record');
      return;
    }
    const defaults = Object.fromEntries(related.fields.map((item) => [
      item.name,
      item.defaultToday ? formatDate(new Date()) : (item.default ?? '')
    ]));
    if (field.dependsOn && field.optionForeignKey && form[field.dependsOn]) {
      defaults[field.optionForeignKey] = form[field.dependsOn];
    }
    setQuickAddForm(defaults);
    setQuickAddField(field);
  }

  function updateQuickAddField(field, value) {
    setQuickAddForm((current) => {
      const next = { ...current, [field.name]: value };
      if (field.name === 'openingBalance') next.balance = value || 0;
      if (field.name === 'paymentTerms' && value !== 'Credit') next.creditDays = '';
      for (const dependent of (quickAddConfig?.fields || []).filter((candidate) => candidate.dependsOn === field.name)) {
        next[dependent.name] = '';
      }
      return next;
    });
  }

  const reorderField = config?.listReorder;
  const canReorder = Boolean(isCrud && reorderField && !search.trim() &&
    listFilters.every((filter) => !filters[filter.name]));
  const [orderDrafts, setOrderDrafts] = useState({});

  useEffect(() => {
    setOrderDrafts({});
  }, [data]);

  const reorder = useMutation({
    mutationFn: async ({ row, direction, value }) => {
      if (value !== undefined) {
        const next = Number(value);
        if (!Number.isInteger(next) || next < 0) {
          throw new ClientValidationError('Order must be a whole number of 0 or greater');
        }
        if (Number(row[reorderField]) === next) return;
        await api.put(`/resources/${resource}/${row.id}`, { [reorderField]: next }, { skipErrorToast: true });
        return;
      }
      const rows = [...data];
      const index = rows.findIndex((item) => item.id === row.id);
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      if (index < 0 || swapIndex < 0 || swapIndex >= rows.length) return;
      const [moved] = rows.splice(index, 1);
      rows.splice(swapIndex, 0, moved);
      const updates = rows
        .map((item, order) => ({ id: item.id, value: order + 1, previous: item[reorderField] }))
        .filter((item) => Number(item.previous) !== item.value);
      await Promise.all(updates.map((item) =>
        api.put(`/resources/${resource}/${item.id}`, { [reorderField]: item.value }, { skipErrorToast: true })
      ));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resource', resource] });
    },
    onError: (mutationError) => {
      const message = mutationError instanceof ClientValidationError
        ? mutationError.message
        : formatApiError(mutationError, `Unable to update ${config.singular.toLowerCase()} order`);
      toast[mutationError instanceof ClientValidationError ? 'warning' : 'error'](message);
    }
  });

  function commitOrder(row) {
    const draft = orderDrafts[row.id];
    if (draft === undefined) return;
    reorder.mutate({ row, value: draft });
  }

  function openCreate() {
    setEditing(null);
    const defaults = Object.fromEntries(config.fields.map((field) => [
      field.name,
      field.defaultToday ? formatDate(new Date()) : (field.default ?? '')
    ]));
    if (reorderField) {
      const nextOrder = data.reduce((max, row) => Math.max(max, Number(row[reorderField]) || 0), 0) + 1;
      defaults[reorderField] = nextOrder;
    }
    setForm(defaults);
    setDialogOpen(true);
  }

  function openEdit(row) {
    setEditing(row);
    setForm(Object.fromEntries(config.fields.map((field) => {
      let value = row[field.name] ?? '';
      if (field.type === 'date' && value) value = formatDate(value);
      else value = maskFieldValue(field.name, value);
      return [field.name, value];
    })));
    setDialogOpen(true);
  }

  function updateField(field, value) {
    setForm((current) => {
      const next = { ...current, [field.name]: value };
      if (field.name === 'openingBalance' && !editing) next.balance = value || 0;
      if (field.name === 'paymentTerms' && value !== 'Credit') next.creditDays = '';
      for (const dependent of config.fields.filter((candidate) => candidate.dependsOn === field.name)) {
        next[dependent.name] = '';
      }
      return next;
    });
    if (field.focusTarget && value === field.focusWhen) {
      queueMicrotask(() => fieldRefs.current[field.focusTarget]?.focus?.());
    }
  }

  const heading = isCrud ? config.title : title;
  const subtitle = isCrud ? config.description : description;
  const addAction = isCrud ? (
    <Button
      variant="contained"
      startIcon={<Add />}
      onClick={openCreate}
    >
      Add {config.singular}
    </Button>
  ) : null;

  return (
    <Box>
      <PageHeader title={heading} subtitle={subtitle} action={addAction} />
      <Card>
        {(searchable || listFilters.length > 0) && (
          <Box px={1.5} py={1}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              {searchable && (
                <TextField
                  size="small"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={config.searchPlaceholder || 'Search'}
                  sx={{ minWidth: { sm: 280 }, flexGrow: 1 }}
                  slotProps={{ input: { startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> } }}
                />
              )}
              {listFilters.map((filter) => {
                const options = filter.options
                  || (references[filter.name] || []).filter((option) => option.isActive !== false);
                return (
                  <TextField
                    key={filter.name}
                    select
                    size="small"
                    label={filter.label}
                    value={filters[filter.name] ?? ''}
                    onChange={(event) => setFilters((current) => ({
                      ...current,
                      [filter.name]: event.target.value
                    }))}
                    sx={{ minWidth: { xs: '100%', md: 180 } }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {options.map((option) => {
                      const optionValue = typeof option === 'string' ? option : (option.value ?? option.id);
                      const optionLabel = typeof option === 'string'
                        ? splitName(option)
                        : (option.label || option[filter.optionLabel || 'name']);
                      return <MenuItem key={optionValue} value={optionValue}>{optionLabel}</MenuItem>;
                    })}
                  </TextField>
                );
              })}
            </Stack>
          </Box>
        )}
        {filteredData.length ? (
          <TableContainer><Table>
            <TableHead><TableRow>
              {columns.map(([key, label]) => <TableCell key={key}>{label}</TableCell>)}
              {isCrud && <TableCell align="right">Actions</TableCell>}
            </TableRow></TableHead>
            <TableBody>{filteredData.map((row, index) => <TableRow hover key={row.id ?? index}>
              {columns.map(([key]) => (
                <TableCell key={key}>
                  {key === reorderField && canReorder ? (
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                      <TextField
                        size="small"
                        type="number"
                        value={orderDrafts[row.id] ?? row[key] ?? ''}
                        disabled={reorder.isPending}
                        onChange={(event) => setOrderDrafts((current) => ({
                          ...current,
                          [row.id]: event.target.value
                        }))}
                        onBlur={() => commitOrder(row)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') event.currentTarget.blur();
                        }}
                        sx={{ width: 88 }}
                        slotProps={{ htmlInput: { min: 0, step: 1 } }}
                      />
                      <Tooltip title="Move up">
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === 0 || reorder.isPending}
                            onClick={() => reorder.mutate({ row, direction: 'up' })}
                          >
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === filteredData.length - 1 || reorder.isPending}
                            onClick={() => reorder.mutate({ row, direction: 'down' })}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ) : format(row[key], key)}
                </TableCell>
              ))}
              {isCrud && <TableCell align="right">
                <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton></Tooltip>
                {!config.noDelete && <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => setDeleting(row)}><Delete fontSize="small" /></IconButton></Tooltip>}
              </TableCell>}
            </TableRow>)}</TableBody>
          </Table></TableContainer>
        ) : !error && <Typography textAlign="center" color="text.secondary" py={8}>No records found.</Typography>}
      </Card>

      {isCrud && <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth={config.compactDialog ? 'xs' : 'md'}
      >
        <DialogTitle>{editing ? 'Edit' : 'Add'} {config.singular}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Grid container spacing={2}>
            {config.fields.filter((field) => isFieldVisible(field, form)).map((field) => (
              <Grid key={field.name} size={{ xs: 12, sm: config.compactDialog ? 12 : (field.span || 6) }}>
                {field.quickAdd ? (
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <ResourceField
                        field={field}
                        value={form[field.name]}
                        options={(field.options || references[field.name] || []).filter((option) => {
                          if (typeof option === 'object' && option.isActive === false
                            && String(option.id) !== String(form[field.name])) return false;
                          return !field.dependsOn || !field.optionForeignKey
                            || String(option[field.optionForeignKey]) === String(form[field.dependsOn]);
                        })}
                        disabled={Boolean(
                          (editing && field.immutable) ||
                          field.readOnly ||
                          (field.dependsOn && !form[field.dependsOn])
                        )}
                        inputRef={(element) => { fieldRefs.current[field.name] = element; }}
                        onChange={(value) => updateField(field, value)}
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      aria-label={`Add ${crudResources[field.resource]?.singular || 'new record'}`}
                      onClick={() => openQuickAdd(field)}
                      style={{ marginTop: field.quickAddTopMargin ?? field.topMargin ?? 0 }}
                      sx={{ height: 40, minWidth: 40, px: 0, flexShrink: 0, fontSize: 22 }}
                    >
                      +
                    </Button>
                  </Stack>
                ) : (
                  <ResourceField
                    field={field}
                    value={form[field.name]}
                    options={(field.options || references[field.name] || []).filter((option) => {
                      if (typeof option === 'object' && option.isActive === false
                        && String(option.id) !== String(form[field.name])) return false;
                      return !field.dependsOn || !field.optionForeignKey
                        || String(option[field.optionForeignKey]) === String(form[field.dependsOn]);
                    })}
                    disabled={Boolean(
                      (editing && field.immutable) ||
                      field.readOnly ||
                      (field.dependsOn && !form[field.dependsOn])
                    )}
                    inputRef={(element) => { fieldRefs.current[field.name] = element; }}
                    onChange={(value) => updateField(field, value)}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel (Alt+C)</Button>
          <Button variant="contained" disabled={save.isPending} onClick={() => save.mutate()}>
            {editing ? 'Update (Alt+U)' : 'Save (Alt+U)'}
          </Button>
        </DialogActions>
      </Dialog>}

      {isCrud && <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove this {config.singular.toLowerCase()}? Referenced master records will be deactivated rather than physically deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={remove.isPending} onClick={() => remove.mutate()}>Remove</Button>
        </DialogActions>
      </Dialog>}

      {isCrud && quickAddConfig && (
        <Dialog
          open={Boolean(quickAddField)}
          onClose={() => setQuickAddField(null)}
          fullWidth
          maxWidth={quickAddConfig.compactDialog ? 'xs' : 'md'}
        >
          <DialogTitle>Add {quickAddConfig.singular}</DialogTitle>
          <DialogContent sx={{ pt: '16px !important' }}>
            <Grid container spacing={2}>
              {quickAddConfig.fields.filter((field) => isFieldVisible(field, quickAddForm)).map((field) => (
                <Grid key={field.name} size={{ xs: 12, sm: quickAddConfig.compactDialog ? 12 : (field.span || 6) }}>
                  <ResourceField
                    field={field}
                    value={quickAddForm[field.name]}
                    options={(field.options || quickAddReferences[field.name] || []).filter((option) =>
                      !field.dependsOn || !field.optionForeignKey ||
                      String(option[field.optionForeignKey]) === String(quickAddForm[field.dependsOn])
                    )}
                    disabled={Boolean(
                      field.readOnly ||
                      (field.dependsOn && !quickAddForm[field.dependsOn])
                    )}
                    inputRef={field.name === (quickAddConfig.focusField || 'name')
                      ? (element) => { quickAddNameRef.current = element; }
                      : undefined}
                    onChange={(value) => updateQuickAddField(field, value)}
                  />
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setQuickAddField(null)}>Cancel</Button>
            <Button variant="contained" disabled={quickAddSave.isPending} onClick={() => quickAddSave.mutate()}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

function ResourceField({ field, value, options, disabled, onChange, inputRef }) {
  if (field.type === 'image') {
    return <ImageField field={field} value={value} disabled={disabled} onChange={onChange} />;
  }
  if (field.type === 'boolean') {
    return <FormControlLabel control={<Switch checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />} label={field.label} />;
  }
  if (field.type === 'select') {
    return <TextField select fullWidth required={field.required} disabled={disabled} label={field.label}
      value={value ?? ''} inputRef={inputRef} onChange={(event) => onChange(event.target.value)}
      sx={{
        mt: field.topMargin,
        ...(disabled && { '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } })
      }}>
      {!field.required && <MenuItem value="">None</MenuItem>}
      {options.map((option) => {
        const optionValue = typeof option === 'string' ? option : (option.value ?? option.id);
        const optionLabel = typeof option === 'string'
          ? splitName(option)
          : (option.label || option[field.optionLabel || 'name']);
        return <MenuItem key={optionValue} value={optionValue}>{optionLabel}</MenuItem>;
      })}
    </TextField>;
  }
  return <TextField
    fullWidth
    required={field.required}
    disabled={disabled}
    inputRef={inputRef}
    type={field.type === 'number' || field.type === 'email' ? field.type : 'text'}
    multiline={field.type === 'multiline'}
    minRows={field.type === 'multiline' ? 2 : undefined}
    label={field.label}
    placeholder={
      field.type === 'date' ? DATE_FORMAT_HINT
        : isPhoneField(field.name) ? '0300-1234567'
          : isCnicField(field.name) ? '12345-1234567-1'
            : undefined
    }
    value={value ?? ''}
    onChange={(event) => {
      const next = event.target.value;
      if (isPhoneField(field.name)) onChange(maskPhone(next));
      else if (isCnicField(field.name)) onChange(maskCnic(next));
      else onChange(shouldCapitalizeField(field) ? capitalizeWords(next) : next);
    }}
    onFocus={(event) => event.target.select()}
    slotProps={
      field.type === 'date' || isPhoneField(field.name) || isCnicField(field.name)
        ? {
          ...(field.type === 'date' ? { inputLabel: { shrink: true } } : {}),
          htmlInput: { inputMode: 'numeric' }
        }
        : undefined
    }
    sx={{
      mt: field.topMargin,
      ...(disabled && { '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } })
    }}
  />;
}

function ImageField({ field, value, disabled, onChange }) {
  const [preview, setPreview] = useState('');

  useEffect(() => {
    if (value instanceof File) {
      const objectUrl = URL.createObjectURL(value);
      setPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    if (typeof value === 'string' && value) {
      const apiOrigin = (import.meta.env.VITE_API_URL || 'http://localhost:5001/api').replace(/\/api\/?$/, '');
      setPreview(`${apiOrigin}${value}`);
    } else {
      setPreview('');
    }
    return undefined;
  }, [value]);

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box
        component={preview ? 'img' : 'div'}
        src={preview || undefined}
        alt={preview ? 'Product preview' : undefined}
        sx={{
          width: 88,
          height: 88,
          objectFit: 'cover',
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover'
        }}
      />
      <Box>
        <Button component="label" variant="outlined" disabled={disabled}>
          {value ? `Change ${field.label}` : `Choose ${field.label}`}
          <input
            hidden
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(event) => onChange(event.target.files?.[0] || '')}
          />
        </Button>
        {value instanceof File && (
          <Typography variant="body2" color="text.secondary" mt={1}>{value.name}</Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          JPEG, PNG, or WebP · maximum 3 MB
        </Typography>
      </Box>
    </Stack>
  );
}

class ClientValidationError extends Error {}

function matchesCondition(condition, form) {
  if (!condition) return false;
  return form[condition.field] === condition.equals;
}

function isFieldVisible(field, form) {
  if (!field.visibleWhen) return true;
  return matchesCondition(field.visibleWhen, form);
}

function isBlank(value) {
  return value === undefined || value === null || value === '';
}

function splitName(value) {
  return value.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
}

function format(value, column) {
  if (value == null || value === '') return '—';
  if (column === 'taxType') return value === 'R' ? 'Register' : value === 'N' ? 'Non Register' : String(value);
  if (isPhoneField(column)) return maskPhone(value);
  if (isCnicField(column)) return maskCnic(value);
  if (/date|at$/i.test(column) && !Number.isNaN(Date.parse(value))) return formatDate(value);
  if (/price|cost|total|amount|balance/i.test(column) && typeof value === 'number') {
    return `Rs ${value.toLocaleString()}`;
  }
  return String(value);
}
